import {Link} from '@shopify/hydrogen';
import classNames from 'classnames';

const DEFAULT_CLASSES =
  'whitespace-nowrap block m-0 w-full items-center justify-center font-medium text-center px-4 rounded disabled:border-gray-300 disabled:bg-gray-300 disabled:cursor-not-allowed';

const VARIANT_CLASSES = {
  primary:
    'py-4 text-white bg-green-shopify hover:bg-green-shopify-hover active:bg-gray-700',
  secondary:
    'py-4 bg-gray-shopify text-green-shopify hover:bg-gray-shopify-hover active:bg-gray-100 border border-2 border-green-shopify',
  plain: 'py-2 bg-transparent text-black',
  action: 'py-2 bg-blue-action text-white rounded-full',
} as const;

export const BUTTON_PRIMARY_CLASSES = `${DEFAULT_CLASSES} ${VARIANT_CLASSES.primary}`;
export const BUTTON_SECONDARY_CLASSES = `${DEFAULT_CLASSES} ${VARIANT_CLASSES.secondary}`;

const ExternalIcon = () => (
  <svg
    className={classNames('ml-3', 'fill-current', 'text-white')}
    width="15"
    height="14"
    viewBox="0 0 15 14"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path d="M8.11963 0.000976562C7.56734 0.000976562 7.11963 0.448692 7.11963 1.00098C7.11963 1.55326 7.56734 2.00098 8.11963 2.00098H10.7054L4.41252 8.29387C4.022 8.68439 4.022 9.31756 4.41252 9.70808C4.80305 10.0986 5.43621 10.0986 5.82674 9.70808L12.1196 3.41519V6.00098C12.1196 6.55326 12.5673 7.00098 13.1196 7.00098C13.6719 7.00098 14.1196 6.55326 14.1196 6.00098V1.00098C14.1196 0.448692 13.6719 0.000976562 13.1196 0.000976562H8.11963Z" />
    <path d="M2.11963 2.00098C1.01506 2.00098 0.119629 2.89641 0.119629 4.00098V12.001C0.119629 13.1055 1.01506 14.001 2.11963 14.001H10.1196C11.2242 14.001 12.1196 13.1055 12.1196 12.001V9.00098C12.1196 8.44869 11.6719 8.00098 11.1196 8.00098C10.5673 8.00098 10.1196 8.44869 10.1196 9.00098V12.001H2.11963V4.00098L5.11963 4.00098C5.67191 4.00098 6.11963 3.55326 6.11963 3.00098C6.11963 2.44869 5.67191 2.00098 5.11963 2.00098H2.11963Z" />
  </svg>
);

const LoadingSVG = () => (
  <svg
    role="status"
    className={classNames(
      'mr-2',
      'inline',
      'h-8',
      'w-8',
      'animate-spin',
      'fill-gray-600',
      'text-gray-200',
      'dark:fill-gray-300',
      'dark:text-gray-600',
    )}
    viewBox="0 0 100 101"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M100 50.5908C100 78.2051 77.6142 100.591 50 100.591C22.3858 100.591 0 78.2051 0 50.5908C0 22.9766 22.3858 0.59082 50 0.59082C77.6142 0.59082 100 22.9766 100 50.5908ZM9.08144 50.5908C9.08144 73.1895 27.4013 91.5094 50 91.5094C72.5987 91.5094 90.9186 73.1895 90.9186 50.5908C90.9186 27.9921 72.5987 9.67226 50 9.67226C27.4013 9.67226 9.08144 27.9921 9.08144 50.5908Z"
      fill="currentColor"
    />
    <path
      d="M93.9676 39.0409C96.393 38.4038 97.8624 35.9116 97.0079 33.5539C95.2932 28.8227 92.871 24.3692 89.8167 20.348C85.8452 15.1192 80.8826 10.7238 75.2124 7.41289C69.5422 4.10194 63.2754 1.94025 56.7698 1.05124C51.7666 0.367541 46.6976 0.446843 41.7345 1.27873C39.2613 1.69328 37.813 4.19778 38.4501 6.62326C39.0873 9.04874 41.5694 10.4717 44.0505 10.1071C47.8511 9.54855 51.7191 9.52689 55.5402 10.0491C60.8642 10.7766 65.9928 12.5457 70.6331 15.2552C75.2735 17.9648 79.3347 21.5619 82.5849 25.841C84.9175 28.9121 86.7997 32.2913 88.1811 35.8758C89.083 38.2158 91.5421 39.6781 93.9676 39.0409Z"
      fill="currentFill"
    />
  </svg>
);

/**
 * A client component that sets the primary and secondary classes for button components
 */
export default function Button({
  className,
  label,
  handleClick,
  url,
  variant = 'primary',
  testId,
  disabled = false,
  loading = false,
}: {
  className?: string;
  label: string;
  url?: string;
  testId?: string;
  variant?: keyof typeof VARIANT_CLASSES;
  handleClick?: React.MouseEventHandler<HTMLButtonElement>;
  disabled?: boolean;
  loading?: boolean;
}) {
  if (!handleClick && !url) {
    return null;
  }

  const isExternal = url
    ? url.indexOf('://') > 0 || url.indexOf('//') === 0
    : false;

  const classes = classNames(
    DEFAULT_CLASSES,
    VARIANT_CLASSES[variant],
    className,
    {
      ['flex']: isExternal,
    },
  );

  if (isExternal) {
    return (
      <a href={url} className={classes} target="_blank" rel="noreferrer">
        {label}
        <ExternalIcon />
      </a>
    );
  }

  if (handleClick) {
    return (
      <button
        className={classes}
        onClick={handleClick}
        type="button"
        disabled={loading || disabled}
        data-testid={testId}
      >
        {loading ? <LoadingSVG /> : label}
      </button>
    );
  }

  return (
    <Link to={url || '#'} className={classes} data-testid={testId}>
      {label}
    </Link>
  );
}
